import type {ValueEditorOptions} from './kp-value-editor/types';
import type {TextValueEditorTypeMap} from './internal/editors/text/types';
import type {NumberValueEditorTypeMap} from './internal/editors/number/types';
import type {ObjectValueEditorTypeMap} from './internal/meta-editors/object/types';
import type {ListValueEditorTypeMap} from './internal/meta-editors/list/types';
import type {GenerableTextValueEditorTypeMap} from './internal/editors/generable-text/types';
import type {TextOrSelectValueEditorTypeMap} from './internal/editors/text-or-select/types';
import type {TextWithMetadataValueEditorTypeMap} from './internal/editors/text-with-metadata/types';
import type {ScannableTextValueEditorTypeMap} from './internal/editors/scannable-text/types';
import type {YearValueEditorTypeMap} from './internal/editors/year/types';
import type {DateValueEditorTypeMap} from './internal/editors/date/types';
import type {MarkdownValueEditorTypeMap} from 'shared/value-editors/internal/editors/markdown/types';
import type {PasswordValueEditorTypeMap} from './internal/editors/password/types';
import type {BooleanValueEditorTypeMap} from './internal/editors/boolean/types';
import type {HiddenValueEditorTypeMap} from './internal/editors/hidden/types';
import type {RangeValueEditorTypeMap} from './internal/editors/range/types';
import type {AutocompleteValueEditorTypeMap} from './internal/editors/autocomplete/types';
import type {SearchOrEditValueEditorTypeMap} from './internal/editors/search-or-edit/types';
import type {NumberRangeValueEditorTypeMap} from './internal/editors/number-range/types';
import type {SingleAcceptableValueEditorTypeMap} from './internal/editors/single-acceptable/types';
import type {MultipleAcceptableValueEditorTypeMap} from './internal/editors/multiple-acceptable/types';
import type {AcceptableRootValueEditorTypeMap} from './internal/editors/acceptable-root/types';
import type {PriceValueEditorTypeMap} from './internal/editors/price/types';
import type {
    ValueEditorErrorMessagesLocalizations
} from 'shared/value-editors/internal/errors/error-messages-localization.service';
import type {ValueEditorLocalizations} from 'shared/value-editors/localizations/types';
import type {TelephoneNumberValueEditorTypeMap} from 'shared/value-editors/internal/editors/telephone-number/types';
import type {InlineRecordSearchValueEditorTypeMap} from 'src/features/record/inline-record-search/types';
import type {DateRangeValueEditorTypeMap} from 'shared/value-editors/internal/editors/datetime-range/types';

export type ValueEditorType = keyof ValueEditorTypeMap;

// portaro specific aliases
export type DefinedValueEditorTypeAliases =
    'search-text' |
    'access-number' |
    'signature' |
    'user' |
    'editable-record-search' |
    'record-search' |
    'field7' |
    'field8' |
    'authority-field8' |
    'smart-list' |
    'velocity-template';

// dynamic aliases + test cases
export type DynamicValueEditorTypeAliases = string;

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export type ValueEditorTypeOrAlias = ValueEditorType | DefinedValueEditorTypeAliases | DynamicValueEditorTypeAliases;

export type MetaEditorTypeMap = ObjectValueEditorTypeMap & ListValueEditorTypeMap;
export type ElementaryEditorTypeMap =
    TextValueEditorTypeMap &
    NumberValueEditorTypeMap &
    GenerableTextValueEditorTypeMap &
    TextOrSelectValueEditorTypeMap &
    TextWithMetadataValueEditorTypeMap &
    YearValueEditorTypeMap &
    DateValueEditorTypeMap &
    ScannableTextValueEditorTypeMap &
    MarkdownValueEditorTypeMap &
    PasswordValueEditorTypeMap &
    BooleanValueEditorTypeMap &
    HiddenValueEditorTypeMap &
    RangeValueEditorTypeMap &
    AutocompleteValueEditorTypeMap &
    SearchOrEditValueEditorTypeMap &
    NumberRangeValueEditorTypeMap &
    SingleAcceptableValueEditorTypeMap &
    MultipleAcceptableValueEditorTypeMap &
    AcceptableRootValueEditorTypeMap &
    PriceValueEditorTypeMap &
    TelephoneNumberValueEditorTypeMap &
    DateRangeValueEditorTypeMap;
export type CustomEditorTypeMap = InlineRecordSearchValueEditorTypeMap;

export type ValueEditorTypeMap = MetaEditorTypeMap & ElementaryEditorTypeMap & CustomEditorTypeMap;

export enum ValueEditorSize {
    MD = 'md',
    SM = 'sm',
    XS = 'xs'
}

export type DefaultOptions<OPT> =
    Required<Pick<OPT, Exclude<keyof OPT, keyof ValueEditorOptions>>>
    & Partial<ValueEditorOptions>;

export type CustomValueEditorLocalizations<LOC extends ValueEditorLocalizations> =
    Partial<LOC>
    | Partial<ValueEditorErrorMessagesLocalizations>